'use client';

import { useDisclosure } from '@chakra-ui/react';
import CustomDialog from '../CustomDialog/CustomDialog';
import { forwardRef, useImperativeHandle, type PropsWithChildren } from 'react';

type ConfirmedDialogProps = {
	trigger: React.ReactNode;
	header: {
		title: string;
	};
} & PropsWithChildren;

export type ConfirmedDialogRef = {
	open:  () => void;
	close: () => void;
};

type ConfirmedDialogComponent = React.ForwardRefExoticComponent<
  ConfirmedDialogProps & React.RefAttributes<ConfirmedDialogRef>
> & {
	Header: typeof CustomDialog.Header;
	Body:   typeof CustomDialog.Body;
	Footer: typeof CustomDialog.Footer;
};

const ConfirmedDialog = forwardRef<ConfirmedDialogRef, ConfirmedDialogProps>(
	({ trigger, header, children }, ref) => {
		const {
			open, onOpen, onClose, setOpen,
		} = useDisclosure();

		useImperativeHandle(ref, () => ({
			open:  onOpen,
			close: onClose,
		}));

		return (
			<CustomDialog.Root open={open} onOpenChange={e => setOpen(e.open)}>
				<CustomDialog.Trigger>
					{trigger}
				</CustomDialog.Trigger>
				<CustomDialog.Portal>
					<CustomDialog.Content>
						<CustomDialog.Header title={header.title} />
						{children}
					</CustomDialog.Content>
				</CustomDialog.Portal>
			</CustomDialog.Root>
		);
	},
) as ConfirmedDialogComponent;

ConfirmedDialog.displayName = 'ConfirmedDialog';
ConfirmedDialog.Header = CustomDialog.Header;
ConfirmedDialog.Body = CustomDialog.Body;
ConfirmedDialog.Footer = CustomDialog.Footer;

export default ConfirmedDialog;